/**
 * The chapter rail. Chapters have no `title` field — the heading is authored inside the text block —
 * so the rail labels a chapter positionally and describes it with a snippet of its own text. Every
 * action is positional too: stored `data` is unvalidated, so a chapter may have no readable `id`.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { AssignmentChapter } from './store/interfaces'

import { useAssignmentEditorStore } from './store'

const CHAPTER_SUMMARY_LENGTH = 40

interface ChapterListItem {
  id: string
  /** 0-based — what the store's positional mutations take. */
  index: number
  /** 1-based. */
  position: number
  /** `Chapter 3`. */
  label: string
  /** Opening characters of the first text block. */
  summary: string
  isSelected: boolean
  isFirstInOrder: boolean
  isLastInOrder: boolean
}

interface UseChapterListReturn {
  refChapters: ComputedRef<ChapterListItem[]>
  refHasChapters: ComputedRef<boolean>
  selectChapter: (index: number) => void
  addChapter: () => void
  removeChapter: (index: number) => void
  moveChapterUp: (index: number) => void
  moveChapterDown: (index: number) => void
}

/** Stored `data` is unvalidated — a `value: null`, a non-array `content` or a non-object chapter
 *  all reach here; every read is guarded. */
export const summariseChapter = (chapter: AssignmentChapter): string => {
  const content: unknown = chapter?.content
  const blocks = Array.isArray(content) ? content as AssignmentChapter['content'] : []
  const text = blocks.find(block => block?.type === 'text')
  const raw: unknown = text?.type === 'text' ? text.value : ''
  const value = typeof raw === 'string' ? raw.trim() : ''

  if (!value) {
    return blocks.length ? 'No text yet' : 'Empty chapter'
  }

  return value.length > CHAPTER_SUMMARY_LENGTH
    ? `${value.slice(0, CHAPTER_SUMMARY_LENGTH)}…`
    : value
}

export const useChapterList = (): UseChapterListReturn => {
  const store = useAssignmentEditorStore()

  const refChapters = computed<ChapterListItem[]>(() => {
    const chapters = store.getChapters

    // A malformed chapter is still listed positionally: the numbering is the author's only address.
    return chapters.map((chapter, index) => ({
      id: chapter?.id ?? '',
      index,
      position: index + 1,
      label: `Chapter ${index + 1}`,
      summary: summariseChapter(chapter),
      isSelected: !!chapter?.id && chapter.id === store.getSelectedChapterId,
      isFirstInOrder: index === 0,
      isLastInOrder: index === chapters.length - 1,
    }))
  })

  const refHasChapters = computed<boolean>(() => store.hasChapters)

  const selectChapter = (index: number): void => {
    store.selectChapterAt(index)
  }

  const addChapter = (): void => {
    store.addChapter()
  }

  const removeChapter = (index: number): void => {
    store.removeChapterAt(index)
  }

  const moveChapterUp = (index: number): void => {
    store.moveChapterAt(index, -1)
  }

  const moveChapterDown = (index: number): void => {
    store.moveChapterAt(index, 1)
  }

  return {
    refChapters,
    refHasChapters,
    selectChapter,
    addChapter,
    removeChapter,
    moveChapterUp,
    moveChapterDown,
  }
}
